import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

export default function Paquetes() {
  const { usuario } = useAuth()
  const [paquetes, setPaquetes] = useState([])
  const [oficinas, setOficinas] = useState([])
  const [contactos, setContactos] = useState([])
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(null)
  const [qrModal, setQrModal] = useState(null) // paquete con QR abierto
  const [firmaModal, setFirmaModal] = useState(null) // ver firma de retiro
  const pollingRef = useRef(null)
  const [filtroEstado, setFiltroEstado] = useState('recibido')
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({
    oficina_id: '', contacto_id: '', destinatario_custom: '',
    numero_envio: '', fecha_recepcion: new Date().toISOString().split('T')[0], observaciones: ''
  })

  const edificioId = usuario?.rol === 'admin' ? null : usuario?.edificio_id

  useEffect(() => { fetchOficinas(); fetchPaquetes() }, [])
  useEffect(() => { fetchPaquetes() }, [filtroEstado])

  const fetchOficinas = async () => {
    let q = supabase.from('oficinas').select('id, numero, titular, email_contacto, email_contacto_2, edificio_id, activa, edificios(nombre)').eq('activa', true).order('numero')
    if (edificioId) q = q.eq('edificio_id', edificioId)
    const { data } = await q
    setOficinas(data || [])
  }

  const fetchContactos = async (oficinaId) => {
    const { data } = await supabase.from('contactos_oficina').select('*')
      .eq('oficina_id', oficinaId).eq('activo', true).order('apellido')
    setContactos(data || [])
    setForm(f => ({ ...f, contacto_id: '', destinatario_custom: '' }))
  }

  const fetchPaquetes = async () => {
    let q = supabase
      .from('paquetes')
      .select('id, destinatario, numero_envio, fecha_recepcion, fecha_retiro, estado, observaciones, retirado_por_nombre, token_retiro, created_at, oficinas(numero, titular, edificio_id), edificios(nombre)')
      .eq('estado', filtroEstado)
      .order('created_at', { ascending: false })
    if (edificioId) q = q.eq('edificio_id', edificioId)
    const { data } = await q
    setPaquetes(data || [])
  }

  const handleOficinaChange = (oficinaId) => {
    setForm(f => ({ ...f, oficina_id: oficinaId, contacto_id: '', destinatario_custom: '' }))
    if (oficinaId) fetchContactos(oficinaId)
    else setContactos([])
  }

  const handleSubmit = async () => {
    if (!form.oficina_id) return
    const contacto = contactos.find(c => c.id === form.contacto_id)
    const destinatario = contacto ? `${contacto.nombre} ${contacto.apellido}` : form.destinatario_custom
    // Emails del contacto: principal + secundario (ej. asistente)
    const emailsContacto = contacto ? [contacto.email, contacto.email_2].filter(Boolean) : []
    if (!destinatario) return

    setLoading(true)
    const oficina = oficinas.find(o => o.id === form.oficina_id)
    const { error } = await supabase.from('paquetes').insert({
      oficina_id: form.oficina_id,
      edificio_id: oficina.edificio_id,
      destinatario,
      numero_envio: form.numero_envio,
      fecha_recepcion: form.fecha_recepcion,
      observaciones: form.observaciones,
      usuario_id: usuario?.id,
    })

    if (!error) {
      // Enviar email al contacto seleccionado o al email general de la oficina
      const emailsNotif = emailsContacto.length > 0
        ? [...new Set(emailsContacto)]
        : [oficina.email_contacto, oficina.email_contacto_2].filter(Boolean)
      for (const emailNotif of emailsNotif) {
        fetch('/api/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tipo: 'paquete',
            destinatario_email: emailNotif,
            oficina: oficina.numero,
            oficina_titular: oficina.titular,
            edificio: oficina.edificios?.nombre || '',
            datos: { destinatario, numero_envio: form.numero_envio, observaciones: form.observaciones }
          })
        }).catch(e => console.error(e))
      }

      setSuccess('Paquete registrado correctamente')
      setTimeout(() => setSuccess(null), 3000)
      setForm({ oficina_id: '', contacto_id: '', destinatario_custom: '', numero_envio: '', fecha_recepcion: new Date().toISOString().split('T')[0], observaciones: '' })
      setContactos([])
      setShowForm(false)
      fetchPaquetes()
    }
    setLoading(false)
  }

  const handleMostrarQR = (paquete) => {
    setQrModal(paquete)
    // Polling: chequear cada 3s si ya firmó
    pollingRef.current = setInterval(async () => {
      const { data } = await supabase.from('paquetes').select('estado, retirado_por_nombre, firma_url').eq('id', paquete.id).single()
      if (data?.estado === 'retirado') {
        clearInterval(pollingRef.current)
        setQrModal(null)
        setSuccess(`Retiro confirmado por ${data.retirado_por_nombre}`)
        setTimeout(() => setSuccess(null), 4000)
        fetchPaquetes()
      }
    }, 3000)
  }

  const handleCerrarQR = () => {
    clearInterval(pollingRef.current)
    setQrModal(null)
  }

  const handleVerFirma = async (p) => {
    const { data } = await supabase.from('paquetes').select('firma_url, retirado_por_nombre, fecha_retiro, destinatario, oficinas(numero)').eq('id', p.id).single()
    if (data) setFirmaModal(data)
  }

  const formatDate = (d) => new Date(d).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' })
  const formatDateTime = (d) => new Date(d).toLocaleString('es-AR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })

  const selectedContacto = contactos.find(c => c.id === form.contacto_id)

  return (
    <div className="fade-in">
      {success && (
        <div style={{ position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)', background: 'var(--green)', color: 'white', padding: '14px 28px', borderRadius: 10, fontWeight: 600, fontSize: 14, zIndex: 9999, boxShadow: '0 8px 32px rgba(0,0,0,0.4)', animation: 'slideDown 0.3s ease' }}>
          ✓ {success}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 36, color: 'var(--text)', marginBottom: 4 }}>Paquetería</h1>
          <p style={{ color: 'var(--text3)', fontSize: 14 }}>
            {paquetes.length} paquete{paquetes.length !== 1 ? 's' : ''} {filtroEstado === 'recibido' ? 'pendientes de retiro' : 'retirados'}
          </p>
        </div>
        <button className="btn btn-gold" onClick={() => setShowForm(!showForm)}>
          {showForm ? '✕ Cancelar' : '+ Nuevo paquete'}
        </button>
      </div>

      {showForm && (
        <div className="card fade-in" style={{ marginBottom: 24, borderColor: 'var(--gold)', borderWidth: 1.5 }}>
          <div style={{ fontSize: 11, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 20 }}>REGISTRAR NUEVO PAQUETE</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }} className="mobile-grid-1">
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>OFICINA DESTINATARIA</label>
              <select className="input" value={form.oficina_id} onChange={e => handleOficinaChange(e.target.value)}>
                <option value="">Seleccioná oficina...</option>
                {oficinas.map(o => (
                  <option key={o.id} value={o.id}>
                    {o.numero} — {o.titular} {usuario?.rol === 'admin' ? `(${o.edificios?.nombre})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>DESTINATARIO</label>
              {form.oficina_id && contactos.length > 0 ? (
                <select className="input" value={form.contacto_id} onChange={e => setForm(f => ({ ...f, contacto_id: e.target.value }))}>
                  <option value="">Seleccioná contacto...</option>
                  {contactos.map(c => (
                    <option key={c.id} value={c.id}>{c.nombre} {c.apellido}{c.email ? ` · ${c.email}` : ''}</option>
                  ))}
                </select>
              ) : (
                <input className="input" placeholder={form.oficina_id ? "Sin contactos cargados — escribí el nombre" : "Primero seleccioná una oficina"}
                  value={form.destinatario_custom} onChange={e => setForm(f => ({ ...f, destinatario_custom: e.target.value }))}
                  disabled={!form.oficina_id} />
              )}
              {(selectedContacto?.email || selectedContacto?.email_2) && (
                <div style={{ fontSize: 11, color: 'var(--gold)', marginTop: 4, fontFamily: 'DM Mono, monospace' }}>
                  ✓ Se notificará a {[selectedContacto.email, selectedContacto.email_2].filter(Boolean).join(' y ')}
                </div>
              )}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>NÚMERO DE ENVÍO</label>
              <input className="input" placeholder="Ej: OCA-123456789" value={form.numero_envio}
                onChange={e => setForm(f => ({ ...f, numero_envio: e.target.value }))} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>FECHA DE RECEPCIÓN</label>
              <input className="input" type="date" value={form.fecha_recepcion}
                onChange={e => setForm(f => ({ ...f, fecha_recepcion: e.target.value }))} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>OBSERVACIONES (opcional)</label>
              <input className="input" placeholder="Ej: Frágil, requiere firma, etc." value={form.observaciones}
                onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))} />
            </div>
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-outline btn-sm" onClick={() => setShowForm(false)}>Cancelar</button>
            <button className="btn btn-gold btn-sm" onClick={handleSubmit}
              disabled={loading || !form.oficina_id || (!form.contacto_id && !form.destinatario_custom)}>
              Registrar paquete
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['recibido', 'Pendientes'], ['retirado', 'Retirados']].map(([val, label]) => (
          <button key={val} className={`btn btn-sm ${filtroEstado === val ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setFiltroEstado(val)}>
            {label}
          </button>
        ))}
      </div>

      {paquetes.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '64px 0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
          <div style={{ color: 'var(--text3)', fontSize: 14 }}>
            {filtroEstado === 'recibido' ? 'No hay paquetes pendientes' : 'No hay paquetes retirados'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {paquetes.map(p => (
            <div key={p.id} className="card" style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px' }}>
              <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>📦</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{p.destinatario}</span>
                  <span className="badge badge-gold" style={{ fontSize: 10 }}>Of. {p.oficinas?.numero}</span>
                  {usuario?.rol === 'admin' && <span className="badge badge-gray" style={{ fontSize: 10 }}>{p.edificios?.nombre}</span>}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>
                  {p.numero_envio && <span style={{ marginRight: 12 }}>#{p.numero_envio}</span>}
                  Recibido: {formatDateTime(p.created_at)}
                  {p.observaciones && <span style={{ marginLeft: 12, color: 'var(--text2)' }}>· {p.observaciones}</span>}
                </div>
              </div>
              {filtroEstado === 'recibido' && (
                <button className="btn btn-gold btn-sm" onClick={() => handleMostrarQR(p)}>
                  Entregar 📲
                </button>
              )}
              {filtroEstado === 'retirado' && (
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono, monospace', marginBottom: 4 }}>
                    Retirado<br />{p.fecha_retiro ? formatDateTime(p.fecha_retiro) : (p.created_at ? formatDateTime(p.created_at) : '—')}
                  </div>
                  {p.retirado_por_nombre && (
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, marginTop: 4 }} onClick={() => handleVerFirma(p)}>
                      Ver firma ✍️
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal QR */}
      {qrModal && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'var(--bg2)', borderRadius: 16, padding: '24px 20px', width: '100%', maxWidth: 340, textAlign: 'center', border: '1.5px solid var(--gold)' }}>
            <div style={{ fontSize: 11, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 8 }}>RETIRO DE PAQUETE</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 4 }}>{qrModal.destinatario}</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>Of. {qrModal.oficinas?.numero} · {qrModal.edificios?.nombre}</div>

            {/* QR generado con API pública */}
            <div style={{ background: '#fff', borderRadius: 12, padding: 8, display: 'inline-block', marginBottom: 16 }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(window.location.origin + '/retirar/' + qrModal.token_retiro)}`}
                alt="QR de retiro"
                style={{ width: 220, height: 220, display: 'block' }}
              />
            </div>

            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
              Pedile al destinatario que escanee este QR con su celular y firme la recepción.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', marginBottom: 24 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--gold)', animation: 'pulse 1.5s infinite' }} />
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>Esperando firma...</div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={handleCerrarQR}>Cancelar</button>
              <button className="btn btn-outline btn-sm" style={{ flex: 1 }} onClick={() => { navigator.clipboard?.writeText(window.location.origin + '/retirar/' + qrModal.token_retiro) }}>
                Copiar link
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal ver firma - usando createPortal para evitar scroll issues */}
      {firmaModal && createPortal(
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(0,0,0,0.8)', 
          zIndex: 9999, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          overflow: 'hidden'
        }} onClick={() => setFirmaModal(null)}>
          <div style={{ 
            background: 'var(--bg2)', 
            borderRadius: 16, 
            padding: '28px 24px', 
            maxWidth: 500, 
            width: '90%', 
            maxHeight: '80vh',
            overflowY: 'auto'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 11, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 8 }}>COMPROBANTE DE RETIRO</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{firmaModal.destinatario}</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 16 }}>
              Of. {firmaModal.oficinas?.numero} · {firmaModal.fecha_retiro ? formatDateTime(firmaModal.fecha_retiro) : '—'}
            </div>
            {firmaModal.retirado_por_nombre && (
              <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 12, fontFamily: 'DM Mono, monospace' }}>
                RETIRADO POR: {firmaModal.retirado_por_nombre}
              </div>
            )}
            <div style={{ background: '#fff', borderRadius: 10, padding: 12, marginBottom: 20, textAlign: 'center' }}>
              <img src={firmaModal.firma_url} alt="Firma" style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: 6, objectFit: 'contain' }} />
            </div>
            <button className="btn btn-ghost btn-sm" style={{ width: '100%' }} onClick={() => setFirmaModal(null)}>Cerrar</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
